import { Auth } from "@aws-amplify/auth"
import { FunctionComponent, MouseEventHandler, useContext, useState } from "react"
import { Container, Nav, Navbar } from "react-bootstrap"
import { GiHamburgerMenu } from "react-icons/gi"
import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom"
import styled from "styled-components"
import { AppContext, AppProvider } from "./components/AppContext"
import Icon from "./components/Icon"
import PrivateComponent from "./components/PrivateComponent"
import SideMenu from "./components/SideMenu"
import { Group } from "./data/models.interface"
import logo from "./images/logo.png"
import AccessControllerPage from "./pages/AccessControllerPage"
import AccessControllersPage from "./pages/AccessControllersPage"
import DoorPage from "./pages/DoorPage"
import DoorsPage from "./pages/DoorsPage"
import EntitiesPage from "./pages/EntitiesPage"
import EntityPage from "./pages/EntityPage"
import HomePage from "./pages/HomePage"
import KeycardDoorsPage from "./pages/KeycardDoorsPage"
import OpenDoorPage from "./pages/OpenDoorPage"
import PassesPage from "./pages/PassesPage"
import PassPage from "./pages/PassPage"
import RolePage from "./pages/RolePage"
import RolesPage from "./pages/RolesPage"
import SignInPage from "./pages/SignInPage"
import SwipeRecordsPage from "./pages/SwipeRecordsPage"
import TenantPage from "./pages/TenantPage"
import TenantsPage from "./pages/TenantsPage"

Auth.configure({
	region: "us-east-1",
	userPoolId: process.env.REACT_APP_USER_POOL_ID,
	userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
	authenticationFlowType: "USER_PASSWORD_AUTH",
})

/**
 * Every page in the app should have access to the AppContext and have the top menu.
 * The top menu contains the side menu and a sign out link.
 * The rest of the Content is based on if the user is logged in and what their url path is.
 */
const App: FunctionComponent = () => {
	return (
		<AppProvider>
			<TopMenu />
			<Content />
		</AppProvider>
	)
}
export default App

interface ITopMenu {}
const TopMenu: FunctionComponent<ITopMenu> = () => {
	const [isSideMenuVisible, SetSideMenuVisible] = useState(false)
	return (
		<>
			<Navbar variant="dark" bg="dark">
				<Container fluid>
					<Icon
						component={GiHamburgerMenu}
						color="#a5a5a5"
						hoverColor="white"
						onClick={() => SetSideMenuVisible(!isSideMenuVisible)}
					/>
					<Navbar.Brand href="https://norhart.com/hero">
						<Logo src={logo} className="App-logo" alt="Hero" />
					</Navbar.Brand>
					<Nav className="justify-content-end">
						<SignoutLink />
					</Nav>
				</Container>
			</Navbar>
			<SideMenu showNavBar={isSideMenuVisible} />
		</>
	)
}

const Logo = styled.img`
	height: 40px;
	margin-top: -10px;
	margin-bottom: -10px;
	filter: brightness(0) invert(1);
	opacity: 0.7;
`

const SignoutLink = () => {
	const { isAuthenticated, setIsAuthenticated, setGroups } = useContext(AppContext)
	const handleSignout: MouseEventHandler = (event) => {
		event.preventDefault()
		Auth.signOut().then(() => {
			setGroups([])
			setIsAuthenticated(false)
		})
	}
	// hide the sign out link if the user is not authenticated
	if (!isAuthenticated) return null
	return (
		<Nav.Link href="#logout" onClick={handleSignout}>
			Sign Out
		</Nav.Link>
	)
}

interface IContent {}
const Content: FunctionComponent<IContent> = () => {
	const { isAuthenticated } = useContext(AppContext)
	// always send the user to the sign in page if they are not authenticated
	if (!isAuthenticated) {
		return <SignInPage />
	} else {
		return <AppRouter />
	}
}

const AppRouter = () => {
	return (
		<RouterProvider
			router={createBrowserRouter(
				createRoutesFromElements(
					<>
						<Route path="/" element={<HomePage />} errorElement={<HomePage />} />
						<Route
							path="access-controllers"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<AccessControllersPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="access-controllers/:accessControllerId"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<AccessControllerPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="doors"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<DoorsPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="doors/:doorId"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<DoorPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="open-door"
							element={
								<PrivateComponent groups={[Group.AccessControlRemoteAccess, Group.GlobalAdmin]} redirect="/">
									<OpenDoorPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="passes"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<PassesPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="passes/:passId"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<PassPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="roles"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<RolesPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="roles/:roleId"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<RolePage />
								</PrivateComponent>
							}
						/>
						<Route
							path="entities"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<EntitiesPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="entities/:entityId"
							element={
								<PrivateComponent
									groups={[Group.AccessControlManager, Group.AccessControlAdmin, Group.GlobalAdmin]}
									redirect="/">
									<EntityPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="tenants"
							element={
								<PrivateComponent
									groups={[
										Group.AccessControl,
										Group.AccessControlManager,
										Group.AccessControlAdmin,
										Group.GlobalAdmin,
									]}
									redirect="/">
									<TenantsPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="tenants/:tenantId"
							element={
								<PrivateComponent
									groups={[
										Group.AccessControl,
										Group.AccessControlManager,
										Group.AccessControlAdmin,
										Group.GlobalAdmin,
									]}
									redirect="/">
									<TenantPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="keycard-doors"
							element={
								<PrivateComponent
									groups={[
										Group.AccessControl,
										Group.AccessControlManager,
										Group.AccessControlAdmin,
										Group.GlobalAdmin,
									]}
									redirect="/">
									<KeycardDoorsPage />
								</PrivateComponent>
							}
						/>
						<Route
							path="swipe-records"
							element={
								<PrivateComponent
									groups={[
										Group.AccessControl,
										Group.AccessControlManager,
										Group.AccessControlAdmin,
										Group.GlobalAdmin,
									]}
									redirect="/">
									<SwipeRecordsPage />
								</PrivateComponent>
							}
						/>
					</>,
				),
			)}
		/>
	)
}
